<template>
  <div
    class="snappy-dropdown"
    :class="{'snappy-dropdown--open': open}"
    @click="toggleOpen"
    v-click-outside="closeDropdown"
  >
    <button
      class="btn snappy-dropdown__toggle"
      :style="triggerStyles"
      :class="[light ? 'snappy-dropdown__toggle--light' : !inverted ? 'btn-primary' : 'btn-outline-primary']"
      type="button"
    >
      <slot name="trigger">{{ text }}</slot>
      <i class="fas fa-chevron-down snappy-dropdown__icon"></i>
    </button>
    <ul class="dropdown-menu snappy-dropdown__menu">
      <slot name="content">
        <li v-for="(item, index) in filteredItems" :key="item.text">
          <span v-tooltip="item.disabled ? item.tooltip : ''">
            <label
              v-if="item.children"
              class="snappy-dropdown__item"
              :class="{'snappy-dropdown__item--disabled': item.disabled}"
              @click.stop
            >
              {{ item.text }}
              <input
                type="checkbox"
                class="snappy-dropdown__checkbox"
                v-model="checkboxes"
                :value="`checkbox_${index}`"
              />
              <i class="fas fa-chevron-down snappy-dropdown__subicon"></i>
              <ul class="snappy-dropdown__submenu">
                <li v-for="child in item.children" :key="child.text" :class="child.itemClass">
                  <span v-tooltip="child.tooltip">
                    <button
                      v-if="child.actionName"
                      @click="onCallAction(child.actionName)"
                      class="snappy-dropdown__sublink"
                      :class="{'snappy-dropdown__sublink--disabled': child.disabled}"
                    >
                      {{ child.text }}
                    </button>
                    <a
                      v-else
                      :href="child.href"
                      class="snappy-dropdown__sublink"
                      :class="{'snappy-dropdown__sublink--disabled': child.disabled}"
                      >{{ child.text }}</a
                    >
                  </span>
                </li>
              </ul>
            </label>

            <template v-else>
              <button
                v-if="item.actionName"
                @click="onCallAction(item.actionName)"
                class="snappy-dropdown__item"
                :class="{'snappy-dropdown__item--disabled': item.disabled}"
              >
                {{ item.text }}
              </button>
              <a
                v-else
                class="snappy-dropdown__item"
                :class="{'snappy-dropdown__item--disabled': item.disabled}"
                :href="item.href"
                >{{ item.text }}</a
              >
            </template>
          </span>
        </li>
      </slot>
    </ul>
  </div>
</template>

<script>
import {ClickOutside} from '../directives/ClickOutside';
export default {
  name: 'snappy-dropdown',
  directives: {ClickOutside},
  data() {
    return {
      open: false,
      checkboxes: [],
    };
  },
  props: {
    text: {
      type: String,
    },
    buttonFontSize: {
      type: Number,
      default: 18,
    },
    inverted: {
      type: Boolean,
    },
    items: {
      type: Array,
    },
    availability: {
      type: String,
    },
    crm: {
      type: String,
    },
    light: Boolean,
    fleetCatchlightIntegrationEnabled: Boolean,
    userIsCatchlightEnabled: Boolean,
  },
  computed: {
    triggerStyles() {
      return {fontSize: this.buttonFontSize + 'px'};
    },
    dropdownItems() {
        const items = this.items || [
            {
                text: 'Import',
                children: [
                    {
                        text: 'Import From File',
                        actionName: 'importFromFile',
                        itemClass: 'importFromFile',
                    },
                    {
                        text: 'Import From CRM',
                        href: '/import/select-groups/',
                        disabled: !this.activeCrm,
                        tooltip: !this.activeCrm ? this.noCrmTooltip : null,
                    },
                    {
                        text: 'View History',
                        href: '/inventory/contacts/imports',
                    },
                ],
            },
            {
                text: 'Sync (2-way)',
                disabled: !this.activeCrm,
                tooltip: this.noCrmTooltip,
                children: [
                    {text: 'Sync Groups', href: '/sync/select-groups'},
                    {text: 'View History', href: '/sync/management'},
                ],
            },
            {text: 'Export Contacts', actionName: 'export'},
        ];

        if (this.fleetCatchlightIntegrationEnabled && this.userIsCatchlightEnabled) {
            items.push({
                text: 'Enrich all Contacts with Catchlight',
                actionName: 'enrich-all',
            });
        }

        return items;
    },
    available() {
      return this.availability ? JSON.parse(this.availability) : null;
    },
    syncAvailable() {
      return !!this.available?.['two_way_sync'];
    },
    filteredItems() {
      if (!this.availability) return this.dropdownItems;
      return !this.syncAvailable ? this.dropdownItems.filter(item => !item.text.includes('Sync')) : this.dropdownItems;
    },
    activeCrm() {
      try {
        const crmData = JSON.parse(this.crm);
        return Object.keys(crmData).length;
      } catch {
        return [];
      }
    },
    noCrmTooltip() {
      return 'You have no CRM connected. Please connect a CRM to do this action.';
    },
  },
  methods: {
    toggleOpen() {
      this.open = !this.open;
      this.checkboxes = [];
    },
    closeDropdown() {
      this.open = false;
      this.checkboxes = [];
    },
    onCallAction(actionName) {
      this.$emit('do-action', actionName);
      setTimeout(this.closeDropdown);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../sass/web/_variables.scss';

.snappy-dropdown {
  display: inline-block;
  position: relative;
  &__toggle {
    width: 100%;
    height: 100%;
    padding: 9px 16px;
    min-width: 130px;
    gap: 5px;
    &--light {
      background: #b5e6e5;
      color: #399999;
      outline: none;
      border: none;
      &:hover {
        background: #399999;
        color: #b5e6e5;
      }
    }
  }
  &__icon,
  &__subicon {
    transition: all 0.25s;
  }
  &__icon {
    font-size: 14px;
    margin-left: 5px;
  }
  &__menu {
    min-width: 100%;
    border: none;
    padding: 0;
    position: absolute;
    top: 100%;
    left: 0;
    display: none;
    z-index: 1000;
    box-shadow: 4px 4px 12px rgba(0, 0, 0, 0.16);
    list-style: none;
    background-color: #fff;
    border-radius: 4px;
  }
  &__item,
  &__sublink {
    width: 100%;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: $dove-grey-color;
    padding: 0 16px;
    text-decoration: none !important;
    background: $white-color;
    position: relative;
    white-space: nowrap;
    border-radius: 0;
    margin: 0;

    &--disabled {
      color: $silver-color;
      pointer-events: none;
    }

    &:hover {
      background: #e0f2f3;
    }
  }
  &--open &__menu {
    display: inline-block;
  }
  &--open &__toggle {
    background: $cyan-color;
    border-color: $cyan-color;
    color: $white-color;
  }

  &--open &__toggle--light {
    background: #399999;
    color: #b5e6e5;
  }

  &--open &__icon {
    transform: scale(-1);
  }

  &__submenu {
    position: absolute;
    left: 100%;
    top: 0;
    min-width: 100%;
    margin: 0;
    display: none;
    border-radius: 4px;
    box-shadow: 12px 6px 12px rgba(0, 0, 0, 0.16);
    list-style: none;
  }
  &__checkbox {
    display: none;
  }

  &__checkbox:checked ~ &__submenu {
    display: block;
  }
  &__checkbox:checked ~ &__subicon {
    transform: rotate(-90deg);
  }
}
</style>
